/**
 * CoachPlaybook - Basketball Sport Module
 * Version: 0.6.0
 * Description: Complete basketball configuration — positions, actions, formations, age groups
 */

const basketball = {
  id: 'basketball',
  name: 'Basketball',
  icon: '🏀',
  accentColor: '#f97316',
  surfaceType: 'court',

  // Court views available
  views: ['half', 'full'],
  defaultView: 'half',
  orientation: ['vertical', 'horizontal'],

  // Default player counts
  playersPerSide: 5,
  maxDefenders: 5,

  // Age group configurations
  ageGroups: [
    { id: 'u8', label: 'U8 (3v3)', players: 3, courtSize: 'half', shotClock: null, periods: 4, periodLength: 6 },
    { id: 'u10', label: 'U10 (4v4)', players: 4, courtSize: 'half', shotClock: null, periods: 4, periodLength: 6 },
    { id: 'u12', label: 'U12 (5v5)', players: 5, courtSize: 'full', shotClock: null, periods: 4, periodLength: 8 },
    { id: 'middle', label: 'Middle School', players: 5, courtSize: 'full', shotClock: null, periods: 4, periodLength: 8 },
    { id: 'high', label: 'High School', players: 5, courtSize: 'full', shotClock: 35, periods: 4, periodLength: 8 },
    { id: 'college', label: 'College', players: 5, courtSize: 'full', shotClock: 30, periods: 2, periodLength: 20 },
    { id: 'pro', label: 'Pro/NBA', players: 5, courtSize: 'full', shotClock: 24, periods: 4, periodLength: 12 },
    { id: 'fiba', label: 'FIBA', players: 5, courtSize: 'full', shotClock: 24, periods: 4, periodLength: 10 }
  ],

  // Positions
  positions: [
    { id: 'PG', name: 'Point Guard', short: 'PG', number: 1, defaultColor: '#3b82f6', role: 'offense' },
    { id: 'SG', name: 'Shooting Guard', short: 'SG', number: 2, defaultColor: '#10b981', role: 'offense' },
    { id: 'SF', name: 'Small Forward', short: 'SF', number: 3, defaultColor: '#f59e0b', role: 'offense' },
    { id: 'PF', name: 'Power Forward', short: 'PF', number: 4, defaultColor: '#f97316', role: 'offense' },
    { id: 'C', name: 'Center', short: 'C', number: 5, defaultColor: '#8b5cf6', role: 'offense' }
  ],

  // Default spread formation (half court, percentage-based coordinates)
  defaultFormation: {
    name: 'Spread',
    positions: {
      PG: { x: 50, y: 80, hasBall: true },
      SG: { x: 85, y: 50, hasBall: false },
      SF: { x: 15, y: 50, hasBall: false },
      PF: { x: 75, y: 28, hasBall: false },
      C:  { x: 50, y: 22, hasBall: false }
    }
  },

  // Common formations
  formations: [
    {
      id: 'spread',
      name: 'Spread / 5-Out',
      type: 'offense',
      positions: { PG: { x: 50, y: 80 }, SG: { x: 85, y: 50 }, SF: { x: 15, y: 50 }, PF: { x: 75, y: 28 }, C: { x: 50, y: 22 } }
    },
    {
      id: 'horns',
      name: 'Horns',
      type: 'offense',
      positions: { PG: { x: 50, y: 82 }, SG: { x: 90, y: 45 }, SF: { x: 10, y: 45 }, PF: { x: 35, y: 55 }, C: { x: 65, y: 55 } }
    },
    {
      id: '1-4-high',
      name: '1-4 High',
      type: 'offense',
      positions: { PG: { x: 50, y: 85 }, SG: { x: 85, y: 55 }, SF: { x: 15, y: 55 }, PF: { x: 65, y: 55 }, C: { x: 35, y: 55 } }
    },
    {
      id: '1-3-1',
      name: '1-3-1',
      type: 'offense',
      positions: { PG: { x: 50, y: 82 }, SG: { x: 80, y: 50 }, SF: { x: 20, y: 50 }, PF: { x: 50, y: 50 }, C: { x: 50, y: 20 } }
    },
    {
      id: 'box',
      name: 'Box',
      type: 'offense',
      positions: { PG: { x: 50, y: 85 }, SG: { x: 70, y: 50 }, SF: { x: 30, y: 50 }, PF: { x: 70, y: 25 }, C: { x: 30, y: 25 } }
    },
    {
      id: 'stack',
      name: 'Stack',
      type: 'offense',
      positions: { PG: { x: 50, y: 85 }, SG: { x: 50, y: 65 }, SF: { x: 50, y: 50 }, PF: { x: 50, y: 35 }, C: { x: 50, y: 20 } }
    },
    {
      id: 'motion-strong',
      name: 'Motion Strong',
      type: 'offense',
      positions: { PG: { x: 50, y: 82 }, SG: { x: 85, y: 55 }, SF: { x: 15, y: 55 }, PF: { x: 65, y: 30 }, C: { x: 50, y: 15 } }
    }
  ],

  // Complete action library
  actions: {
    offense: [
      { id: 'move', name: 'Move', icon: '💨', lineStyle: 'dashed', category: 'movement' },
      { id: 'cut', name: 'Cut', icon: '✂️', lineStyle: 'solid', category: 'movement' },
      { id: 'dribble', name: 'Dribble', icon: '🏀', lineStyle: 'solid-ball', category: 'ball' },
      { id: 'pass', name: 'Pass', icon: '➡️', lineStyle: 'arrow', category: 'ball' },
      { id: 'bounce-pass', name: 'Bounce Pass', icon: '⬇️', lineStyle: 'arrow-bounce', category: 'ball' },
      { id: 'lob-pass', name: 'Lob Pass', icon: '⬆️', lineStyle: 'arrow-lob', category: 'ball' },
      { id: 'skip-pass', name: 'Skip Pass', icon: '↗️', lineStyle: 'arrow-skip', category: 'ball' },
      { id: 'entry-pass', name: 'Entry Pass', icon: '📥', lineStyle: 'arrow', category: 'ball' },
      { id: 'screen', name: 'Screen', icon: '🛡️', lineStyle: 'screen', category: 'screen' },
      { id: 'back-screen', name: 'Back Screen', icon: '🔙', lineStyle: 'screen', category: 'screen' },
      { id: 'ball-screen', name: 'Ball Screen', icon: '🏀🛡️', lineStyle: 'screen', category: 'screen' },
      { id: 'down-screen', name: 'Down Screen', icon: '⬇️🛡️', lineStyle: 'screen', category: 'screen' },
      { id: 'flare-screen', name: 'Flare Screen', icon: '↗️🛡️', lineStyle: 'screen', category: 'screen' },
      { id: 'cross-screen', name: 'Cross Screen', icon: '↔️🛡️', lineStyle: 'screen', category: 'screen' },
      { id: 'shoot', name: 'Shoot', icon: '🎯', lineStyle: 'shot', category: 'scoring' },
      { id: 'layup', name: 'Layup', icon: '🏀⬆️', lineStyle: 'shot', category: 'scoring' },
      { id: 'dunk', name: 'Dunk', icon: '💥', lineStyle: 'shot', category: 'scoring' },
      { id: 'floater', name: 'Floater', icon: '☁️', lineStyle: 'shot', category: 'scoring' },
      { id: 'post-up', name: 'Post Up', icon: '💪', lineStyle: 'solid', category: 'post' },
      { id: 'seal', name: 'Seal / Pin', icon: '📌', lineStyle: 'solid', category: 'post' },
      { id: 'flash', name: 'Flash', icon: '⚡', lineStyle: 'dashed', category: 'movement' },
      { id: 'curl', name: 'Curl', icon: '🌀', lineStyle: 'curved', category: 'movement' },
      { id: 'fade', name: 'Fade', icon: '↘️', lineStyle: 'dashed', category: 'movement' },
      { id: 'slip', name: 'Slip Screen', icon: '💨', lineStyle: 'dashed', category: 'screen' },
      { id: 'pop', name: 'Pop', icon: '🔝', lineStyle: 'dashed', category: 'movement' },
      { id: 'roll', name: 'Roll', icon: '🔄', lineStyle: 'solid', category: 'movement' },
      { id: 'replace', name: 'Replace / Fill', icon: '🔁', lineStyle: 'dashed', category: 'movement' },
      { id: 'handoff', name: 'Handoff', icon: '🤝', lineStyle: 'arrow-short', category: 'ball' },
      { id: 'pick-up', name: 'Pick Up Dribble', icon: '✋', lineStyle: 'none', category: 'ball' },
      { id: 'jab', name: 'Jab Step', icon: '👟', lineStyle: 'none', category: 'footwork' },
      { id: 'pivot', name: 'Pivot', icon: '🔄', lineStyle: 'none', category: 'footwork' },
      { id: 'euro-step', name: 'Euro Step', icon: '🇪🇺', lineStyle: 'zigzag', category: 'footwork' },
      { id: 'step-back', name: 'Step Back', icon: '⬅️', lineStyle: 'dashed', category: 'footwork' },
      { id: 'crossover', name: 'Crossover', icon: '↔️', lineStyle: 'zigzag', category: 'ball' },
      { id: 'spin-move', name: 'Spin Move', icon: '🌀', lineStyle: 'curved', category: 'ball' },
      { id: 'behind-back', name: 'Behind the Back', icon: '🔙', lineStyle: 'curved', category: 'ball' },
      { id: 'hesitation', name: 'Hesitation', icon: '⏸️', lineStyle: 'none', category: 'ball' },
      { id: 'in-out', name: 'In and Out', icon: '↕️', lineStyle: 'zigzag', category: 'ball' },
      { id: 'rebound', name: 'Crash Boards', icon: '📏', lineStyle: 'dashed', category: 'rebounding' },
      { id: 'box-out', name: 'Box Out', icon: '📦', lineStyle: 'none', category: 'rebounding' },
      { id: 'tip', name: 'Tip / Putback', icon: '👆', lineStyle: 'shot', category: 'scoring' },
      { id: 'alley-oop', name: 'Alley-Oop', icon: '🎪', lineStyle: 'arrow-lob', category: 'scoring' }
    ],
    defense: [
      { id: 'deny', name: 'Deny', icon: '🚫', lineStyle: 'solid', category: 'on-ball' },
      { id: 'closeout', name: 'Closeout', icon: '🏃', lineStyle: 'dashed', category: 'on-ball' },
      { id: 'contest', name: 'Contest Shot', icon: '✋', lineStyle: 'none', category: 'on-ball' },
      { id: 'switch', name: 'Switch', icon: '🔀', lineStyle: 'dashed', category: 'team' },
      { id: 'hedge', name: 'Hedge', icon: '🛡️', lineStyle: 'solid', category: 'screen-defense' },
      { id: 'ice', name: 'Ice / Push', icon: '❄️', lineStyle: 'solid', category: 'screen-defense' },
      { id: 'drop', name: 'Drop', icon: '⬇️', lineStyle: 'dashed', category: 'screen-defense' },
      { id: 'blitz', name: 'Blitz / Trap', icon: '⚡', lineStyle: 'solid', category: 'screen-defense' },
      { id: 'show', name: 'Show & Recover', icon: '👁️', lineStyle: 'solid', category: 'screen-defense' },
      { id: 'help', name: 'Help Defense', icon: '🆘', lineStyle: 'dashed', category: 'team' },
      { id: 'rotate', name: 'Rotation', icon: '🔄', lineStyle: 'dashed', category: 'team' },
      { id: 'recover', name: 'Recover', icon: '↩️', lineStyle: 'dashed', category: 'team' },
      { id: 'steal', name: 'Steal', icon: '🤏', lineStyle: 'none', category: 'on-ball' },
      { id: 'block', name: 'Block', icon: '🖐️', lineStyle: 'none', category: 'on-ball' },
      { id: 'deflect', name: 'Deflection', icon: '💫', lineStyle: 'none', category: 'on-ball' },
      { id: 'charge', name: 'Take Charge', icon: '🛑', lineStyle: 'none', category: 'on-ball' },
      { id: 'trap', name: 'Trap', icon: '🪤', lineStyle: 'solid', category: 'team' },
      { id: 'zone-shift', name: 'Zone Shift', icon: '➡️', lineStyle: 'dashed', category: 'zone' },
      { id: 'stunt', name: 'Stunt', icon: '👻', lineStyle: 'dashed', category: 'team' },
      { id: 'dig', name: 'Dig / Tag', icon: '⬇️', lineStyle: 'dashed', category: 'team' },
      { id: 'box-out-def', name: 'Box Out', icon: '📦', lineStyle: 'none', category: 'rebounding' },
      { id: 'crash-boards', name: 'Crash Boards', icon: '📏', lineStyle: 'dashed', category: 'rebounding' }
    ],
    transition: [
      { id: 'fast-break', name: 'Push / Fast Break', icon: '🚀', lineStyle: 'solid', category: 'transition' },
      { id: 'outlet', name: 'Outlet Pass', icon: '📤', lineStyle: 'arrow', category: 'transition' },
      { id: 'rim-run', name: 'Rim Run', icon: '🏃‍♂️', lineStyle: 'solid', category: 'transition' },
      { id: 'trail', name: 'Trail', icon: '🔙', lineStyle: 'dashed', category: 'transition' },
      { id: 'leak-out', name: 'Leak Out', icon: '💧', lineStyle: 'dashed', category: 'transition' },
      { id: 'sprint-back', name: 'Sprint Back', icon: '🏃‍♂️⬅️', lineStyle: 'solid', category: 'transition' },
      { id: 'match-up', name: 'Match Up', icon: '🎯', lineStyle: 'dashed', category: 'transition' }
    ]
  },

  // Play categories
  playTypes: {
    offense: [
      { id: 'set-play', label: 'Set Play' },
      { id: 'motion-offense', label: 'Motion Offense' },
      { id: 'isolation', label: 'Isolation' },
      { id: 'pick-and-roll', label: 'Pick and Roll' },
      { id: 'pick-and-pop', label: 'Pick and Pop' },
      { id: 'post-up', label: 'Post Up' },
      { id: 'fast-break', label: 'Fast Break' },
      { id: 'secondary-break', label: 'Secondary Break' },
      { id: 'press-break', label: 'Press Break' },
      { id: 'zone-offense', label: 'Zone Offense' },
      { id: 'delay', label: 'Delay / Stall' },
      { id: 'lob-play', label: 'Lob Play' },
      { id: 'backdoor', label: 'Backdoor' }
    ],
    defense: [
      { id: 'man-to-man', label: 'Man to Man' },
      { id: 'zone-2-3', label: '2-3 Zone' },
      { id: 'zone-3-2', label: '3-2 Zone' },
      { id: 'zone-1-3-1', label: '1-3-1 Zone' },
      { id: 'zone-1-2-2', label: '1-2-2 Zone' },
      { id: 'matchup-zone', label: 'Matchup Zone' },
      { id: 'full-court-press', label: 'Full Court Press' },
      { id: 'half-court-press', label: 'Half Court Press' },
      { id: 'trap-defense', label: 'Trap Defense' },
      { id: 'box-and-one', label: 'Box and One' },
      { id: 'triangle-and-two', label: 'Triangle and Two' },
      { id: 'diamond-press', label: 'Diamond Press' }
    ],
    outOfBounds: [
      { id: 'blob', label: 'BLOB (Baseline)' },
      { id: 'slob', label: 'SLOB (Sideline)' },
      { id: 'full-court-inbound', label: 'Full Court Inbound' },
      { id: 'ato', label: 'After Timeout' }
    ],
    special: [
      { id: 'buzzer-beater', label: 'Buzzer Beater' },
      { id: 'end-of-quarter', label: 'End of Quarter' },
      { id: 'late-game', label: 'Late Game' },
      { id: 'free-throw', label: 'Free Throw Alignment' },
      { id: 'jump-ball', label: 'Jump Ball' },
      { id: 'tip-off', label: 'Tip Off' }
    ]
  },

  // Stat categories for this sport
  statCategories: [
    { id: 'pts', name: 'Points', short: 'PTS', type: 'counting' },
    { id: 'reb', name: 'Rebounds', short: 'REB', type: 'counting' },
    { id: 'ast', name: 'Assists', short: 'AST', type: 'counting' },
    { id: 'stl', name: 'Steals', short: 'STL', type: 'counting' },
    { id: 'blk', name: 'Blocks', short: 'BLK', type: 'counting' },
    { id: 'to', name: 'Turnovers', short: 'TO', type: 'counting' },
    { id: 'pf', name: 'Personal Fouls', short: 'PF', type: 'counting' },
    { id: 'min', name: 'Minutes', short: 'MIN', type: 'time' },
    { id: 'fgm', name: 'FG Made', short: 'FGM', type: 'counting' },
    { id: 'fga', name: 'FG Attempted', short: 'FGA', type: 'counting' },
    { id: 'tpm', name: '3PT Made', short: '3PM', type: 'counting' },
    { id: 'tpa', name: '3PT Attempted', short: '3PA', type: 'counting' },
    { id: 'ftm', name: 'FT Made', short: 'FTM', type: 'counting' },
    { id: 'fta', name: 'FT Attempted', short: 'FTA', type: 'counting' },
    { id: 'oreb', name: 'Off. Rebounds', short: 'OREB', type: 'counting' },
    { id: 'dreb', name: 'Def. Rebounds', short: 'DREB', type: 'counting' },
    { id: 'plusMinus', name: 'Plus/Minus', short: '+/-', type: 'calculated' }
  ],

  // Scouting skill categories
  scoutingSkills: [
    { id: 'shooting', name: 'Shooting', icon: '🎯' },
    { id: 'driving', name: 'Driving', icon: '🏃' },
    { id: 'passing', name: 'Passing', icon: '➡️' },
    { id: 'defense', name: 'Defense', icon: '🛡️' },
    { id: 'rebounding', name: 'Rebounding', icon: '📏' },
    { id: 'athleticism', name: 'Speed/Athleticism', icon: '⚡' },
    { id: 'bbiq', name: 'Basketball IQ', icon: '🧠' }
  ],

  // Court dimensions (for SVG rendering)
  court: {
    // NBA dimensions in feet
    width: 50,
    length: 94,
    halfLength: 47,
    threePointRadius: 23.75,
    threePointCorner: 22,
    keyWidth: 16,
    keyLength: 19,
    freeThrowRadius: 6,
    rimDiameter: 1.5,
    backboardWidth: 6,
    restrictedAreaRadius: 4,
    centerCircleRadius: 6,
    // Hash marks, baseline, sideline, etc.
    markings: [
      'three-point-line', 'free-throw-line', 'key', 'restricted-area',
      'center-circle', 'half-court-line', 'baseline', 'sideline',
      'backboard', 'rim', 'hash-marks'
    ]
  }
};

export default basketball;
